/**
 * mapId → 맵 테마(MapTheme) 표 — **자동 생성됨. 직접 고치지 말 것.**
 *
 * regenerate: python3 scripts/gen_map_themes.py
 *
 * `maps/*.txt`에는 통행 비트만 있어 지형 종류를 알 수 없다. 그래서 DB
 * `map_ids` 테이블의 영어 지명을 키워드로 훑어 7개 테마로 접어 넣고,
 * 아이소메트릭 바닥/벽을 테마별 팔레트로 칠한다. `underwater` 컬럼이 1인 맵은
 * 이름과 무관하게 'water'다.
 *
 * 'field'로 판정된 맵은 **일부러 표에 넣지 않는다** — mapTheme()이 어차피
 * DEFAULT_MAP_THEME('field')로 폴백하므로 표를 부풀릴 이유가 없다.
 */

export type MapTheme = 'town' | 'field' | 'forest' | 'cave' | 'castle' | 'ship' | 'water';

/** 표에 없는 mapId(이름 없는 맵, 야외로 판정된 맵)가 떨어지는 자리. */
export const DEFAULT_MAP_THEME: MapTheme = 'field';

/** mapId → 테마. DEFAULT_MAP_THEME과 같은 맵은 들어 있지 않다. */
export const MAP_THEMES: Readonly<Record<number, MapTheme>> = {
  0: 'town',  // Talking Island
  1: 'cave',  // Talking Island Dungeon 1F
  2: 'cave',  // Talking Island Dungeon 2F
  3: 'castle',  // Gunter's House
  5: 'ship',  // Talking Island Ship to Aden Mainland
  6: 'ship',  // Aden Mainland Ship to Talking Island
  7: 'cave',  // Mainland Dungeon 1F
  8: 'cave',  // Mainland Dungeon 2F
  9: 'cave',  // Mainland Dungeon 3F
  10: 'cave',  // Mainland Dungeon 4F
  11: 'cave',  // Mainland Dungeon 5F
  12: 'cave',  // Mainland Dungeon 6F
  13: 'cave',  // Mainland Dungeon 7F
  14: 'cave',  // Underground Passage
  15: 'castle',  // Kent Inner Castle
  16: 'cave',  // Hardin's Lab
  17: 'cave',  // Nerupa's Cave
  18: 'cave',  // Dupelgenon Dungeon
  19: 'cave',  // Elven Dungeon 1F
  20: 'cave',  // Elven Dungeon 2F
  21: 'cave',  // Elven Dungeon 3F
  22: 'cave',  // Gerard Test Dungeon
  23: 'cave',  // Windawood Castle Dungeon 1F
  24: 'cave',  // Windawood Castle Dungeon 2F
  25: 'cave',  // Silver Knight Town Caves 1F
  26: 'cave',  // Silver Knight Town Caves 2F
  27: 'cave',  // Silver Knight Town Caves 3F
  28: 'cave',  // Silver Knight Town Caves 4F
  29: 'castle',  // Windawood Inner Castle
  30: 'cave',  // Dragon Valley Caves 1F
  31: 'cave',  // Dragon Valley Caves 2F
  32: 'cave',  // Dragon Valley Caves 3F
  33: 'cave',  // Dragon Valley Caves 4F
  34: 'castle',  // Hall of Honor
  35: 'cave',  // Dragon Valley Caves 5F
  36: 'cave',  // Dragon Valley Caves 6F
  37: 'cave',  // Dragon Valley Caves 7F Lair of Antharas
  38: 'castle',  // Hall of Honor
  39: 'castle',  // Hall of Honor
  40: 'castle',  // Hall of Honor
  41: 'castle',  // Hall of Honor
  43: 'cave',  // Ant Caves
  44: 'cave',  // Ant Caves
  45: 'cave',  // Ant Caves
  46: 'cave',  // Ant Caves
  47: 'cave',  // Ant Caves
  48: 'cave',  // Ant Caves
  49: 'cave',  // Ant Caves
  50: 'cave',  // Ant Caves
  51: 'cave',  // Ant Caves
  52: 'castle',  // Giran Inner Castle
  53: 'cave',  // Giran Prison 1F
  54: 'cave',  // Giran Prison 2F
  55: 'cave',  // Giran Prison 3F
  56: 'cave',  // Giran Prison 4F
  59: 'cave',  // Heine Dungeon 1F
  60: 'cave',  // Heine Dungeon 2F
  61: 'cave',  // Heine Dungeon 3F
  62: 'castle',  // Sanctuary of Eva
  63: 'water',  // Kingdom of Eva
  64: 'castle',  // Heine Inner Castle
  65: 'water',  // Lair of Fafurion
  66: 'castle',  // Iron Gate Guild Inner Castle
  67: 'cave',  // Lair of Valakas
  72: 'cave',  // Crystal Cavern 1F
  73: 'cave',  // Crystal Cavern 2F
  74: 'cave',  // Crystal Cavern 3F
  75: 'castle',  // Ivory Tower 1F
  76: 'castle',  // Ivory Tower 2F
  77: 'castle',  // Ivory Tower 3F
  78: 'castle',  // Ivory Tower 4F Inanimate object Laboratory
  79: 'castle',  // Ivory Tower 5F Black magic Laboratory
  80: 'castle',  // Ivory Tower 6F Monster Seal room
  81: 'castle',  // Ivory Tower 7F Monster Seal room
  82: 'castle',  // Ivory Tower 8F Demon Seal room
  83: 'ship',  // Aden Mainland Ship to Forgotten Island
  84: 'ship',  // Forgotten Island Ship to Aden Mainland
  85: 'cave',  // Singing Island Dungeon
  86: 'cave',  // Hidden Valley Dungeon
  87: 'castle',  // Pago's Room
  88: 'town',  // Giran Arena
  89: 'town',  // Giran Arena
  90: 'town',  // Giran Arena
  91: 'town',  // Talking Island Arena
  92: 'town',  // Gludio Arena
  93: 'town',  // Kent Arena
  94: 'town',  // Windawood Arena
  95: 'town',  // Silver Knight Arena
  96: 'cave',  // Lair of Valakas Arena
  97: 'town',  // Heine Arena
  98: 'town',  // Werldern Arena
  99: 'castle',  // GM Room
  101: 'castle',  // Tower of Insolence 1F
  102: 'castle',  // Tower of Insolence 2F
  103: 'castle',  // Tower of Insolence 3F
  104: 'castle',  // Tower of Insolence 4F
  105: 'castle',  // Tower of Insolence 5F
  106: 'castle',  // Tower of Insolence 6F
  107: 'castle',  // Tower of Insolence 7F
  108: 'castle',  // Tower of Insolence 8F
  109: 'castle',  // Tower of Insolence 9F
  110: 'castle',  // Tower of Insolence 10F
  111: 'castle',  // Tower of Insolence 11F
  112: 'castle',  // Tower of Insolence 12F
  113: 'castle',  // Tower of Insolence 13F
  114: 'castle',  // Tower of Insolence 14F
  115: 'castle',  // Tower of Insolence 15F
  116: 'castle',  // Tower of Insolence 16F
  117: 'castle',  // Tower of Insolence 17F
  118: 'castle',  // Tower of Insolence 18F
  119: 'castle',  // Tower of Insolence 19F
  120: 'castle',  // Tower of Insolence 20F
  121: 'castle',  // Tower of Insolence 21F
  122: 'castle',  // Tower of Insolence 22F
  123: 'castle',  // Tower of Insolence 23F
  124: 'castle',  // Tower of Insolence 24F
  125: 'castle',  // Tower of Insolence 25F
  126: 'castle',  // Tower of Insolence 26F
  127: 'castle',  // Tower of Insolence 27F
  128: 'castle',  // Tower of Insolence 28F
  129: 'castle',  // Tower of Insolence 29F
  130: 'castle',  // Tower of Insolence 30F
  131: 'castle',  // Tower of Insolence 31F
  132: 'castle',  // Tower of Insolence 32F
  133: 'castle',  // Tower of Insolence 33F
  134: 'castle',  // Tower of Insolence 34F
  135: 'castle',  // Tower of Insolence 35F
  136: 'castle',  // Tower of Insolence 36F
  137: 'castle',  // Tower of Insolence 37F
  138: 'castle',  // Tower of Insolence 38F
  139: 'castle',  // Tower of Insolence 39F
  140: 'castle',  // Tower of Insolence 40F
  141: 'castle',  // Tower of Insolence 41F
  142: 'castle',  // Tower of Insolence 42F
  143: 'castle',  // Tower of Insolence 43F
  144: 'castle',  // Tower of Insolence 44F
  145: 'castle',  // Tower of Insolence 45F
  146: 'castle',  // Tower of Insolence 46F
  147: 'castle',  // Tower of Insolence 47F
  148: 'castle',  // Tower of Insolence 48F
  149: 'castle',  // Tower of Insolence 49F
  150: 'castle',  // Tower of Insolence 50F
  151: 'castle',  // Tower of Insolence 51F
  152: 'castle',  // Tower of Insolence 52F
  153: 'castle',  // Tower of Insolence 53F
  154: 'castle',  // Tower of Insolence 54F
  155: 'castle',  // Tower of Insolence 55F
  156: 'castle',  // Tower of Insolence 56F
  157: 'castle',  // Tower of Insolence 57F
  158: 'castle',  // Tower of Insolence 58F
  159: 'castle',  // Tower of Insolence 59F
  160: 'castle',  // Tower of Insolence 60F
  161: 'castle',  // Tower of Insolence 61F
  162: 'castle',  // Tower of Insolence 62F
  163: 'castle',  // Tower of Insolence 63F
  164: 'castle',  // Tower of Insolence 64F
  165: 'castle',  // Tower of Insolence 65F
  166: 'castle',  // Tower of Insolence 66F
  167: 'castle',  // Tower of Insolence 67F
  168: 'castle',  // Tower of Insolence 68F
  169: 'castle',  // Tower of Insolence 69F
  170: 'castle',  // Tower of Insolence 70F
  171: 'castle',  // Tower of Insolence 71F
  172: 'castle',  // Tower of Insolence 72F
  173: 'castle',  // Tower of Insolence 73F
  174: 'castle',  // Tower of Insolence 74F
  175: 'castle',  // Tower of Insolence 75F
  176: 'castle',  // Tower of Insolence 76F
  177: 'castle',  // Tower of Insolence 77F
  178: 'castle',  // Tower of Insolence 78F
  179: 'castle',  // Tower of Insolence 79F
  180: 'castle',  // Tower of Insolence 80F
  181: 'castle',  // Tower of Insolence 81F
  182: 'castle',  // Tower of Insolence 82F
  183: 'castle',  // Tower of Insolence 83F
  184: 'castle',  // Tower of Insolence 84F
  185: 'castle',  // Tower of Insolence 85F
  186: 'castle',  // Tower of Insolence 86F
  187: 'castle',  // Tower of Insolence 87F
  188: 'castle',  // Tower of Insolence 88F
  189: 'castle',  // Tower of Insolence 89F
  190: 'castle',  // Tower of Insolence 90F
  191: 'castle',  // Tower of Insolence 91F
  192: 'castle',  // Tower of Insolence 92F
  193: 'castle',  // Tower of Insolence 93F
  194: 'castle',  // Tower of Insolence 94F
  195: 'castle',  // Tower of Insolence 95F
  196: 'castle',  // Tower of Insolence 96F
  197: 'castle',  // Tower of Insolence 97F
  198: 'castle',  // Tower of Insolence 98F
  199: 'castle',  // Tower of Insolence 99F
  200: 'castle',  // Tower of Insolence 100F
  201: 'cave',  // Mage Quest Dungeon
  202: 'cave',  // Mage Quest Dungeon
  203: 'cave',  // Mage Quest Dungeon
  204: 'cave',  // Mage Quest Dungeon
  209: 'cave',  // Darkelf Dungeon
  210: 'cave',  // Darkelf Dungeon
  211: 'cave',  // Darkelf Dungeon
  213: 'cave',  // Darkelf Dungeon
  217: 'cave',  // Mutant Ant Dungeon
  221: 'cave',  // Gerard Test Dungeon
  237: 'cave',  // Jim's Dungeon
  240: 'cave',  // Kent Castle Dungeon 1F
  241: 'cave',  // Kent Castle Dungeon 2F
  242: 'cave',  // Kent Castle Dungeon 3F
  243: 'cave',  // Kent Castle Dungeon 4F
  248: 'cave',  // Giran Castle Dungeon 1F Taros's Inner Castle
  249: 'cave',  // Giran Castle Dungeon 2F Trap of Greedy Person
  250: 'cave',  // Giran Castle Dungeon 3F Greedy Hall
  251: 'cave',  // Giran Castle Dungeon Taros's Room
  252: 'cave',  // Heine Castle Dungeon Underground Prison of Heine
  253: 'water',  // Heine Castle Dungeon Kingdom of Eva
  254: 'cave',  // Heine Castle Dungeon Barbados's Lair
  255: 'cave',  // Dwarf Castle Dungeon Dwarf Mine
  256: 'cave',  // Dwarf Castle Dungeon Savage Land
  257: 'cave',  // Aden Castle Dungeon 1F Corridor of The Dead
  258: 'cave',  // Aden Castle Dungeon 2F Grave of The Dead
  259: 'cave',  // Aden Castle Dungeon 3F Corridor of The Dead
  270: 'castle',  // Ivory Tower Secret Research Lab
  271: 'cave',  // Crystal Cavern
  272: 'cave',  // Crystal Cavern
  273: 'cave',  // Crystal Cavern
  274: 'cave',  // Crystal Cavern
  275: 'cave',  // Crystal Cavern
  276: 'cave',  // Crystal Cavern
  277: 'cave',  // Crystal Cavern
  278: 'cave',  // Crystal Cavern
  280: 'castle',  // Janus's Ivory Tower 4F Barlog Camp
  281: 'castle',  // Janus's Ivory Tower 5F Barlog Camp
  282: 'castle',  // Janus's Ivory Tower 6F Barlog Camp
  283: 'castle',  // Janus's Ivory Tower 7F Barlog Camp
  284: 'castle',  // Janus's Ivory Tower 8F Barlog Camp
  285: 'castle',  // Janus's Ivory Tower 4F Yahee Camp
  286: 'castle',  // Janus's Ivory Tower 5F Yahee Camp
  287: 'castle',  // Janus's Ivory Tower 6F Yahee Camp
  288: 'castle',  // Janus's Ivory Tower 7F Yahee Camp
  289: 'castle',  // Janus's Ivory Tower 8F Yahee Camp
  300: 'castle',  // Aden Inner Castle
  301: 'cave',  // Aden Sewer
  302: 'cave',  // Sepia Dungeon
  304: 'cave',  // Silent Cavern
  305: 'castle',  // Elder Room
  306: 'cave',  // Dungeon of Evil Thoughts
  307: 'cave',  // Lastavard Dungeon 1F
  308: 'cave',  // Lastavard Dungeon 2F
  309: 'cave',  // Lastavard Dungeon 3F
  310: 'cave',  // Oum Dungeon
  320: 'castle',  // Diad Fortress
  330: 'cave',  // Mineral Dungeon
  340: 'town',  // Gludin Traders Village
  350: 'town',  // Giran Traders Village
  360: 'town',  // Oren Traders Village
  370: 'town',  // Silver Knight Traders Village
  400: 'cave',  // Great Cavern Recluse Village Area
  401: 'cave',  // Great Cavern Resistance Area
  410: 'castle',  // Sanctuary of Evil
  420: 'water',  // Abyss Lake
  430: 'cave',  // Elemental Grave
  441: 'cave',  // Pirate Island Dungeon 1F
  442: 'cave',  // Pirate Island Dungeon 2F
  443: 'cave',  // Pirate Island Dungeon 3F
  444: 'cave',  // Pirate Island Dungeon 4F
  445: 'town',  // Hidden Dock to Pirate Island
  446: 'ship',  // Pirate Island Ship to Hidden Dock
  447: 'ship',  // Hidden Dock Ship to Pirate Island
  451: 'castle',  // Lastavard Castle 1F Meeting Area
  452: 'castle',  // Lastavard Castle 1F Shock Squad Training Area
  453: 'castle',  // Lastavard Castle 1F Evil Power King's Power Room
  454: 'castle',  // Lastavard Castle 1F Wild Animal Torture Room
  455: 'castle',  // Lastavard Castle 1F Wild Animal Training Room
  456: 'castle',  // Lastavard Castle 1F Evil Power Summoning Room
  457: 'castle',  // Lastavard Castle 1F Off-Limits Area of Darkness
  460: 'castle',  // Lastavard Castle 2F Dark Magic Training Area
  461: 'castle',  // Lastavard Castle 2F Dark Magic Laboratory
  462: 'castle',  // Lastavard Castle 2F Evil Spirit King's Power Room
  463: 'castle',  // Lastavard Castle 2F Evil Spirit King's Library
  464: 'castle',  // Lastavard Castle 2F Spirit Summoning Room
  465: 'castle',  // Lastavard Castle 2F Spirit Abode
  466: 'castle',  // Lastavard Castle 2F Dark Spirit Laboratory
  467: 'castle',  // Lastavard Castle 2F Off-Limits Area of Blackness
  468: 'castle',  // Lastavard Castle 2F Off-Limits Area of Blackness
  470: 'castle',  // Lastavard Castle 3F Evil Spirit Altar
  471: 'castle',  // Lastavard Castle 3F Devil Road Altar
  472: 'castle',  // Lastavard Castle 3F Mercenary Training Area
  473: 'castle',  // Lastavard Castle 3F Bright Magic Army Training Area
  474: 'castle',  // Lastavard Castle 3F Oum Experiment Room
  475: 'castle',  // Lastavard Castle 3F Bright Magic King's Power Room
  476: 'castle',  // Lastavard Castle 3F Central Control Room
  477: 'castle',  // Lastavard Castle 3F Devil Road Mercenary Room
  478: 'castle',  // Lastavard Castle 3F Control Area
  479: 'town',  // Lastavard Central Square
  481: 'castle',  // Kamyla's Room
  482: 'cave',  // Franco's Labyrinth
  483: 'cave',  // Closed Jail of Diego
  484: 'cave',  // Underground Jail of Hose
  490: 'cave',  // Lastavard Castle Underground Training Area
  491: 'cave',  // Lastavard Castle Underground Passageway
  492: 'cave',  // Lastavard Castle Underground Assassin King's Power Room
  493: 'cave',  // Lastavard Castle Underground Control Room
  494: 'cave',  // Lastavard Castle Underground Punishment Area
  495: 'cave',  // Lastavard Castle Underground Duel Area
  496: 'cave',  // Lastavard Castle Underground Jail
  509: 'town',  // Chaos Battle Collosseum
  521: 'castle',  // Temple of Shadow Outland
  522: 'castle',  // Temple of Shadow 1F
  523: 'castle',  // Temple of Shadow 2F
  524: 'castle',  // Temple of Shadow 3F
  530: 'castle',  // Lastavard Castle 4F Temple of Gran Kain
  531: 'castle',  // Lastavard Castle 4F Supreme Court Justice Viatas's Room
  532: 'castle',  // Lastavard Castle 4F Open Garden Area
  533: 'castle',  // Lastavard Castle 4F Supreme Court Justice Tiames's Room
  534: 'castle',  // Lastavard Castle 4F Elder Assembly Room
  535: 'castle',  // Dark Elf Holy Place
  536: 'castle',  // Lastavard Castle 3F Off-Limits Area of Darkness
  537: 'castle',  // Doomed Dark Elf Holy Place
  541: 'cave',  // Ant Caves Egg Room A
  542: 'cave',  // Ant Caves Egg Room B
  543: 'cave',  // Ant Caves Queen Room
  551: 'ship',  // Inboard at Ship Grave Area
  552: 'water',  // Inboard at Ship Grave Area
  553: 'ship',  // Inboard at Ship Grave Area
  554: 'ship',  // Inboard at Ship Grave Area
  555: 'water',  // Inboard at Ship Grave Area
  556: 'ship',  // Inboard at Ship Grave Area
  557: 'water',  // Inboard at Ship Grave Area
  558: 'water',  // Deep Sea of Ship Grave Area
  600: 'cave',  // Cave of Desire Outland
  601: 'cave',  // Cave of Desire Lobby
  602: 'castle',  // Barlog's Audience Room
  603: 'cave',  // Barlog Lair
  604: 'water',  // Cave of Desire Water Room
  605: 'cave',  // Cave of Desire Fire Room
  606: 'cave',  // Cave of Desire Air Room
  607: 'cave',  // Cave of Desire Earth Room
  608: 'cave',  // Yahee's Laboratory
  610: 'town',  // Witch's Market
  611: 'town',  // Camp Site
  612: 'forest',  // Forest of Abundance
  620: 'town',  // Netcafe Lobby
  622: 'town',  // Netcafe Villade
  630: 'castle',  // Castle in the Sky
  632: 'castle',  // Brave Man's Sacred Place 1
  653: 'cave',  // Giran Prison 1F for Netcafe
  654: 'cave',  // Giran Prison 2F for Netcafe
  655: 'cave',  // Giran Prison 3F for Netcafe
  656: 'cave',  // Giran Prison 4F for Netcafe
  666: 'cave',  // Hell
  779: 'water',  // Land of Abandoned
  781: 'cave',  // Thebes Pyramid Inside
  782: 'castle',  // Thebes Osiris Altar
  783: 'cave',  // Tikal Ruin
  784: 'castle',  // Tikal Kukulcan Altar
  1002: 'town',  // Dwarven Village
  1005: 'cave',  // Dungeon Entrance of Antharas
  1006: 'cave',  // Dungeon Entrance of Antharas
  1007: 'cave',  // Dungeon Entrance of Antharas
  1008: 'cave',  // Dungeon Entrance of Antharas
  1009: 'cave',  // Dungeon Entrance of Antharas
  1010: 'cave',  // Dungeon Entrance of Antharas
  1011: 'water',  // Dungeon Entrance of Fafurion
  1012: 'water',  // Dungeon Entrance of Fafurion
  1013: 'water',  // Dungeon Entrance of Fafurion
  1014: 'water',  // Dungeon Entrance of Fafurion
  1015: 'water',  // Dungeon Entrance of Fafurion
  1016: 'water',  // Dungeon Entrance of Fafurion
  1017: 'cave',  // Dungeon Entrance of Lindvior
  1018: 'cave',  // Dungeon Entrance of Lindvior
  1019: 'cave',  // Dungeon Entrance of Lindvior
  1020: 'cave',  // Dungeon Entrance of Lindvior
  1021: 'cave',  // Dungeon Entrance of Lindvior
  1022: 'cave',  // Dungeon Entrance of Lindvior
  2000: 'castle',  // Palingenesis Sanctuary 1F
  2001: 'castle',  // Palingenesis Sanctuary 2F
  2002: 'castle',  // Palingenesis Sanctuary 3F
  2003: 'castle',  // Palingenesis Sanctuary 4F
  2006: 'town',  // Playground of Metis
  2100: 'cave',  // Crystal Cavern Entrance
  2101: 'cave',  // Crystal Cavern
  2151: 'cave',  // Crystal Cavern
  2201: 'cave',  // Mysterious Ice Crystal Cave 1F
  2202: 'cave',  // Mysterious Ice Crystal Cave 2F
  2203: 'cave',  // Mysterious Ice Crystal Cave 3F
  2210: 'cave',  // Lair of Valakas
  5001: 'castle',  // Aden Underground house 1
  5002: 'castle',  // Aden Underground house 2
  5003: 'castle',  // Aden Underground house 3
  5004: 'castle',  // Aden Underground house 4
  5005: 'castle',  // Aden Underground house 5
  5006: 'castle',  // Aden Underground house 6
  5007: 'castle',  // Aden Underground house 7
  5008: 'castle',  // Aden Underground house 8
  5009: 'castle',  // Aden Underground house 9
  5010: 'castle',  // Aden Underground house 10
  5011: 'castle',  // Aden Underground house 11
  5012: 'castle',  // Aden Underground house 12
  5013: 'castle',  // Aden Underground house 13
  5014: 'castle',  // Aden Underground house 14
  5015: 'castle',  // Aden Underground house 15
  5016: 'castle',  // Aden Underground house 16
  5017: 'castle',  // Aden Underground house 17
  5018: 'castle',  // Aden Underground house 18
  5019: 'castle',  // Aden Underground house 19
  5020: 'castle',  // Aden Underground house 20
  5021: 'castle',  // Aden Underground house 21
  5022: 'castle',  // Aden Underground house 22
  5023: 'castle',  // Aden Underground house 23
  5024: 'castle',  // Aden Underground house 24
  5025: 'castle',  // Aden Underground house 25
  5026: 'castle',  // Aden Underground house 26
  5027: 'castle',  // Aden Underground house 27
  5028: 'castle',  // Aden Underground house 28
  5029: 'castle',  // Aden Underground house 29
  5030: 'castle',  // Aden Underground house 30
  5031: 'castle',  // Aden Underground house 31
  5032: 'castle',  // Aden Underground house 32
  5033: 'castle',  // Aden Underground house 33
  5034: 'castle',  // Aden Underground house 34
  5035: 'castle',  // Aden Underground house 35
  5036: 'castle',  // Aden Underground house 36
  5037: 'castle',  // Aden Underground house 37
  5038: 'castle',  // Aden Underground house 38
  5039: 'castle',  // Aden Underground house 39
  5040: 'castle',  // Aden Underground house 40
  5041: 'castle',  // Aden Underground house 41
  5042: 'castle',  // Aden Underground house 42
  5043: 'castle',  // Aden Underground house 43
  5044: 'castle',  // Aden Underground house 44
  5045: 'castle',  // Aden Underground house 45
  5046: 'castle',  // Aden Underground house 46
  5047: 'castle',  // Aden Underground house 47
  5048: 'castle',  // Aden Underground house 48
  5049: 'castle',  // Aden Underground house 49
  5050: 'castle',  // Aden Underground house 50
  5051: 'castle',  // Aden Underground house 51
  5052: 'castle',  // Aden Underground house 52
  5053: 'castle',  // Aden Underground house 53
  5054: 'castle',  // Aden Underground house 54
  5055: 'castle',  // Aden Underground house 55
  5056: 'castle',  // Aden Underground house 56
  5057: 'castle',  // Aden Underground house 57
  5058: 'castle',  // Aden Underground house 58
  5059: 'castle',  // Aden Underground house 59
  5060: 'castle',  // Aden Underground house 60
  5061: 'castle',  // Aden Underground house 61
  5062: 'castle',  // Aden Underground house 62
  5063: 'castle',  // Aden Underground house 63
  5064: 'castle',  // Aden Underground house 64
  5065: 'castle',  // Aden Underground house 65
  5066: 'castle',  // Aden Underground house 66
  5067: 'castle',  // Aden Underground house 67
  5068: 'castle',  // Giran Underground house 1
  5069: 'castle',  // Giran Underground house 2
  5070: 'castle',  // Giran Underground house 3
  5071: 'castle',  // Giran Underground house 4
  5072: 'castle',  // Giran Underground house 5
  5073: 'castle',  // Giran Underground house 6
  5074: 'castle',  // Giran Underground house 7
  5075: 'castle',  // Giran Underground house 8
  5076: 'castle',  // Giran Underground house 9
  5077: 'castle',  // Giran Underground house 10
  5078: 'castle',  // Giran Underground house 11
  5079: 'castle',  // Giran Underground house 12
  5080: 'castle',  // Giran Underground house 13
  5081: 'castle',  // Giran Underground house 14
  5082: 'castle',  // Giran Underground house 15
  5083: 'castle',  // Giran Underground house 16
  5084: 'castle',  // Giran Underground house 17
  5085: 'castle',  // Giran Underground house 18
  5086: 'castle',  // Giran Underground house 19
  5087: 'castle',  // Giran Underground house 20
  5088: 'castle',  // Giran Underground house 21
  5089: 'castle',  // Giran Underground house 22
  5090: 'castle',  // Giran Underground house 23
  5091: 'castle',  // Giran Underground house 24
  5092: 'castle',  // Giran Underground house 25
  5093: 'castle',  // Giran Underground house 26
  5094: 'castle',  // Giran Underground house 27
  5095: 'castle',  // Giran Underground house 28
  5096: 'castle',  // Giran Underground house 29
  5097: 'castle',  // Giran Underground house 30
  5098: 'castle',  // Giran Underground house 31
  5099: 'castle',  // Giran Underground house 32
  5100: 'castle',  // Giran Underground house 33
  5101: 'castle',  // Giran Underground house 34
  5102: 'castle',  // Giran Underground house 35
  5103: 'castle',  // Giran Underground house 36
  5104: 'castle',  // Giran Underground house 37
  5105: 'castle',  // Giran Underground house 38
  5106: 'castle',  // Giran Underground house 39
  5107: 'castle',  // Giran Underground house 40
  5108: 'castle',  // Giran Underground house 41
  5109: 'castle',  // Giran Underground house 42
  5110: 'castle',  // Giran Underground house 43
  5111: 'castle',  // Giran Underground house 44
  5112: 'castle',  // Giran Underground house 45
  5113: 'castle',  // Heine Underground house 1
  5114: 'castle',  // Heine Underground house 2
  5115: 'castle',  // Heine Underground house 3
  5116: 'castle',  // Heine Underground house 4
  5117: 'castle',  // Heine Underground house 5
  5118: 'castle',  // Heine Underground house 6
  5119: 'castle',  // Heine Underground house 7
  5120: 'castle',  // Heine Underground house 8
  5121: 'castle',  // Heine Underground house 9
  5122: 'castle',  // Heine Underground house 10
  5123: 'castle',  // Heine Underground house 11
  5140: 'castle',  // Ghost house
  5141: 'castle',  // Ghost house
  5142: 'castle',  // Ghost house
  5191: 'cave',  // Ivory Tower Underground Warehouse
  5192: 'cave',  // Ivory Tower Underground Warehouse
  5193: 'cave',  // Ivory Tower Underground Warehouse
  5194: 'cave',  // Ivory Tower Underground Warehouse
  5195: 'cave',  // Ivory Tower Underground Warehouse
  5196: 'cave',  // Ivory Tower Underground Warehouse
  5197: 'cave',  // Ivory Tower Underground Warehouse
  5198: 'cave',  // Ivory Tower Underground Warehouse
  5199: 'cave',  // Ivory Tower Underground Warehouse
  5303: 'cave',  // dungeon of crack low
  5384: 'cave',  // dungeon of crack middle
  5435: 'cave',  // dungeon of crack high
  5554: 'castle',  // Wedding Hall of Yahee
  7002: 'castle',  // Sanctuary of Farthest (1)
  7003: 'castle',  // Sanctuary of Farthest (2)
  7004: 'castle',  // Sanctuary of Farthest (3)
  7005: 'castle',  // Sanctuary of Farthest (4)
  7006: 'castle',  // Sanctuary of Farthest (5)
  7051: 'town',  // Concealed Fairy Village
  7052: 'forest',  // Concealed Fairy Forest(2)
  7053: 'forest',  // Concealed Fairy Forest(3)
  7054: 'forest',  // Concealed Fairy Forest(4)
  7055: 'forest',  // Depth of Forest
  7057: 'town',  // Summer Resort
  7781: 'castle',  // Air Temple of Fairies
  7782: 'castle',  // Air Temple of Fairies
  8013: 'castle',  // Doromond's Room
  8014: 'castle',  // Training Room
  9000: 'cave',  // Secret Research Lab of The Past
  9100: 'castle',  // Ivory Tower Secret Research Lab - Hardin
  9202: 'castle',  // Ivory Tower Secret Research Lab - Orim
  16384: 'town',  // Inn
  16896: 'town',  // Inn
  17408: 'town',  // Inn
  17920: 'town',  // Inn
  18432: 'town',  // Inn
  18944: 'town',  // Inn
  19456: 'town',  // Inn
  19968: 'town',  // Inn
  20480: 'town',  // Inn
  20992: 'town',  // Inn
  21504: 'town',  // Inn
  22016: 'town',  // Inn
  22528: 'town',  // Inn
  23040: 'town',  // Inn
  23552: 'town',  // Inn
  24064: 'town',  // Inn
  24576: 'town',  // Inn
  25088: 'town',  // Inn
};

/** mapId → 테마. 표에 없거나 mapId가 없으면 DEFAULT_MAP_THEME. */
export function mapTheme(mapId: number | null | undefined): MapTheme {
  if (mapId == null) return DEFAULT_MAP_THEME;
  return MAP_THEMES[mapId] ?? DEFAULT_MAP_THEME;
}
